/**
 * FERP Portal - Apollo Router Metrics Hook
 *
 * Fetches real-time Prometheus metrics from Apollo Router at :9090/metrics
 * and parses them into a usable format for the SubgraphConstellation view.
 *
 * Metrics fetched:
 * - apollo_router_http_requests_total: Total HTTP requests (counter)
 * - apollo_router_cache_hit_count_total: Cache hits (counter)
 * - apollo_router_cache_miss_count_total: Cache misses (counter)
 * - http_server_active_requests: Active concurrent requests (gauge)
 * - apollo_router_http_request_duration_seconds: Latency histogram
 * - apollo_router_graphql_error_total: GraphQL errors (counter)
 */

import { useState, useEffect, useCallback } from 'react';

export interface RouterMetrics {
  totalRequests: number;
  requestsPerMinute: number;
  avgLatency: number; // milliseconds
  errorRate: number; // percentage
  cacheHitRate: number; // percentage
  activeQueries: number;
  supergraphVersion: string;
}

interface ParsedMetrics {
  requests: number;
  cacheHits: number;
  cacheMisses: number;
  activeRequests: number;
  errors: number;
  latencySum: number;
  latencyCount: number;
}

/**
 * Parse Prometheus text format into metrics object
 */
function parsePrometheusMetrics(text: string): ParsedMetrics {
  const lines = text.split('\n');
  const metrics: ParsedMetrics = {
    requests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    activeRequests: 0,
    errors: 0,
    latencySum: 0,
    latencyCount: 0,
  };

  for (const line of lines) {
    // Skip comments and empty lines
    if (line.startsWith('#') || !line.trim()) continue;

    // Parse metric line: metric_name{labels} value
    const match = line.match(/^([a-z_]+)(\{[^}]*\})?\s+([0-9.]+)/);
    if (!match) continue;

    const [, metricName, , value] = match;
    // @ts-expect-error Argument type
    const numValue = parseFloat(value);

    // Total HTTP requests (sum all status codes)
    if (metricName === 'apollo_router_http_requests_total' && line.includes('status="200"')) {
      metrics.requests += numValue;
    }

    // Cache hits/misses (query planner cache)
    if (metricName === 'apollo_router_cache_hit_count_total' && line.includes('query planner')) {
      metrics.cacheHits = numValue;
    }
    if (metricName === 'apollo_router_cache_miss_count_total' && line.includes('query planner')) {
      metrics.cacheMisses = numValue;
    }

    // Active requests (POST method for GraphQL)
    if (metricName === 'http_server_active_requests' && line.includes('POST')) {
      metrics.activeRequests = numValue;
    }

    // GraphQL errors
    if (metricName === 'apollo_router_graphql_error_total') {
      metrics.errors += numValue;
    }

    // Latency histogram (sum and count for average)
    if (metricName === 'apollo_router_http_request_duration_seconds_sum') {
      metrics.latencySum = numValue;
    }
    if (metricName === 'apollo_router_http_request_duration_seconds_count') {
      metrics.latencyCount = numValue;
    }
  }

  return metrics;
}

/**
 * Calculate derived metrics from parsed Prometheus data
 */
function calculateMetrics(parsed: ParsedMetrics, uptimeMinutes: number): RouterMetrics {
  const totalCacheOperations = parsed.cacheHits + parsed.cacheMisses;
  const cacheHitRate = totalCacheOperations > 0 ? (parsed.cacheHits / totalCacheOperations) * 100 : 0;

  const avgLatency =
    parsed.latencyCount > 0 ? (parsed.latencySum / parsed.latencyCount) * 1000 : 0; // Convert to ms

  const requestsPerMinute = uptimeMinutes > 0 ? parsed.requests / uptimeMinutes : 0;

  const errorRate = parsed.requests > 0 ? (parsed.errors / parsed.requests) * 100 : 0;

  return {
    totalRequests: parsed.requests,
    requestsPerMinute: Math.round(requestsPerMinute),
    avgLatency: Math.round(avgLatency),
    errorRate: parseFloat(errorRate.toFixed(2)),
    cacheHitRate: parseFloat(cacheHitRate.toFixed(1)),
    activeQueries: parsed.activeRequests,
    supergraphVersion: '2026.02.01', // TODO: Extract from Router version metric
  };
}

/**
 * Hook to fetch and parse Apollo Router metrics
 */
export function useRouterMetrics(refreshIntervalMs = 5000) {
  const [metrics, setMetrics] = useState<RouterMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [startTime] = useState(Date.now());

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch('/router-metrics');
      if (!response.ok) {
        throw new Error(`Router metrics unavailable: ${response.status}`);
      }

      const text = await response.text();
      const parsed = parsePrometheusMetrics(text);

      // Calculate uptime in minutes (from when hook was mounted)
      const uptimeMinutes = (Date.now() - startTime) / 1000 / 60;

      const calculated = calculateMetrics(parsed, Math.max(uptimeMinutes, 1));
      setMetrics(calculated);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch router metrics:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [startTime]);

  // Initial fetch
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Refresh interval
  useEffect(() => {
    if (refreshIntervalMs <= 0) return;

    const interval = setInterval(fetchMetrics, refreshIntervalMs);
    return () => clearInterval(interval);
  }, [fetchMetrics, refreshIntervalMs]);

  return {
    metrics,
    loading,
    error,
    refetch: fetchMetrics,
  };
}

export default useRouterMetrics;
